// ActivityFromBadge.tsx
/* Un composant pour créer des activités à partir des mouvements de badge. */

// import react
import React, { useEffect, useState } from 'react';

// Import communs et Layout
import {
	SelectField,
	MainButton,
	formatLocalDate,
	parseLocalDate,
} from 'common';

// Import de mouvement de badge
import { BadgeMovementData, badgeMovementsApi } from 'features/badgeMovement';

// Import activité
import { ActivityData, activitiesApi } from 'features/activities';
import AFBActivityCard from './AFBActivityCard';

// Import des affaires
import { projectsApi } from 'features/projects';

import { ServiceData, servicesApi } from 'features/services';

// Import FontAwesome pour les icônes
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faArrowAltCircleLeft,
	faArrowAltCircleRight,
	faArrowRight,
	faPen,
	faPersonArrowDownToLine,
	faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import PageHeader from 'layout/PageHeader';

// Composant ActivityFromBadge
export const ActivityFromBadge: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = useState(true);
	/*const [headerParams, setHeaderParams] = useState({
		projectId: null as number | null,
		fromDate: new Date('2026-01-01').toISOString().split('T')[0],
		toDate: new Date().toISOString().split('T')[0],
	});*/
	const today = new Date();
	const threeDaysAgo = new Date();
	threeDaysAgo.setDate(today.getDate() - 3);

	const [headerParams, setHeaderParams] = useState({
		projectId: null as number | null,
		fromDate: formatLocalDate(threeDaysAgo),
		toDate: formatLocalDate(today),
	});
	const [appliedHeaderParams, setAppliedHeaderParams] = useState(headerParams);

	// Hook badges
	const [selectedBadge, setSelectedBadge] = useState<string | null>('all');
	const [appliedSelectedBadge, setAppliedSelectedBadge] =
		useState(selectedBadge);
	const [badgeMovements, setBadgeMovements] = useState<BadgeMovementData[]>([]);

	// Hook activités
	const [activities, setActivities] = useState<ActivityData[]>([]);
	const [loadingActivity, setLoadingActivity] = useState(true);
	const [selectedActivityId, setSelectedActivityId] = useState<number | null>(
		null
	);
	const [showExistingActivities, setShowExistingActivities] = useState(false);
	const [appliedShowExistingActivities, setAppliedShowExistingActivities] =
		useState(showExistingActivities);
	const [isSavingActivity, setIsSavingActivity] = useState(false);
	const [activitiesValidity, setActivitiesValidity] = useState<
		Record<number, boolean>
	>({});
	type ActivityColor = 'green' | 'yellow' | 'orange' | 'red' | 'blue';
	const [activityColors, setActivityColors] = useState<
		Record<number, ActivityColor>
	>({});
	const [visibleColors, setVisibleColors] = useState<
		Record<ActivityColor, boolean>
	>({
		green: true,
		yellow: true,
		orange: true,
		red: true,
		blue: true,
	});

	// Paramètres pour l'algorithme de regroupement :
	const [minHoursBetweenActivity, setMinHoursBetweenActivity] = useState(11);
	const [maxHoursActivity, setMaxHoursActivity] = useState(16);

	// Compteur pour générer des ids temporaires négatifs
	const nextTemporaryIdRef = React.useRef(-1);

	// Hook du select des numéros de badge
	const [badgesOptions, setBadgesOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);

	// Hooks select project
	const [projectOptions, setProjectOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingProjects, setLoadingProjects] = React.useState(true);

	// Hooks des services (ServiceData)
	const [serviceForfait, setServiceForfait] =
		React.useState<ServiceData | null>(null);
	const [serviceDemiForfait, setServiceDemiForfait] =
		React.useState<ServiceData | null>(null);
	const [serviceESNonFacturable, setServiceESNonFacturable] =
		React.useState<ServiceData | null>(null);
	const [loadingServices, setLoadingServices] = React.useState(true);

	// Légende des couleurs pour le header et les filtres
	const colorLegend: {
		key: ActivityColor;
		label: string;
		description: string;
		textClass: string;
	}[] = [
		{
			key: 'green',
			label: 'Vert',
			description: 'Activité valide',
			textClass: 'text-green-600',
		},
		{
			key: 'yellow',
			label: 'Jaune',
			description: 'entre 10h et 11h',
			textClass: 'text-yellow-600',
		},
		{
			key: 'orange',
			label: 'Orange',
			description: 'entre 11h et 16h',
			textClass: 'text-orange-600',
		},
		{
			key: 'red',
			label: 'Rouge',
			description: "Invalide (pas d'affaire, >16h)",
			textClass: 'text-red-600',
		},
		{
			key: 'blue',
			label: 'Bleu',
			description: 'Activité existante',
			textClass: 'text-blue-600',
		},
	];

	// Loading : on charge les options de badges avec un fetch Api (on garde uniquement les badgeNumber)
	useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		// Filtres pour l'API
		const filters: Record<string, string> = {
			dateTime_from: appliedHeaderParams.fromDate,
			dateTime_to: appliedHeaderParams.toDate,
		};

		badgeMovementsApi
			.listPaginated(token, { limit: 9999, filters })
			.then((res) => {
				// ne garder que les badgeNumber définis et les typer en string, filtrer sans doublons
				const options = res.data
					.map((badge) => badge.badgeNumber)
					.filter((bn): bn is string => typeof bn === 'string' && bn.length > 0)
					.filter((bn, i, arr) => arr.indexOf(bn) === i)
					.sort((a, b) =>
						a.localeCompare(b, 'fr', { numeric: true, sensitivity: 'base' })
					)
					.map((bn) => ({
						key: bn,
						label: bn,
					}));
				// on ajoute une option "Tous" pour ne pas filtrer par badge
				options.unshift({ key: 'all', label: 'Tous les badges' });
				setBadgesOptions(options);
			})
			.finally(() => setLoading(false));
	}, []);

	// Lazy load des options projets en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingProjects) {
			projectsApi
				.listSelect(token)
				.then((items) => {
					setProjectOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingProjects(false));
		}
	}, [loading, loadingProjects]);

	// Lazy load des services en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingServices) {
			servicesApi
				.listPaginated(token, { limit: 999999 })
				.then((res) => {
					// filtrage pour ne garder que le service avec un label défini : Forfait et Demi-forfait
					setServiceForfait(
						res.data.find((service) => service.label === 'Forfait') || null
					);
					setServiceDemiForfait(
						res.data.find((service) => service.label === 'Demi-forfait') || null
					);
					setServiceESNonFacturable(
						res.data.find((service) => service.label === 'ES non facturable') ||
							null
					);
				})
				.catch(console.error)
				.finally(() => setLoadingServices(false));
		}
	}, [loading, loadingServices]);

	// Gestion des changements dans le formulaire (badges, dates, activités) : on recharge les mouvements de badge à chaque changement// Gestion des changements dans le formulaire (badges, dates, activités) : on recharge les mouvements de badge à chaque changement
	useEffect(() => {
		// Vérification du token et du badge sélectionné (toujours valide, vaut "all" si tous les badges)
		const token = localStorage.getItem('token');
		if (!token || !appliedSelectedBadge) return;

		// On charge les mouvements de badge en fonction du badge sélectionné (sauf si "all" est sélectionné, auquel cas on charge tout)
		setLoading(true);
		setLoadingActivity(true);

		const filters: Record<string, string> = {
			beginDate_from: appliedHeaderParams.fromDate,
			beginDate_to: appliedHeaderParams.toDate,
		};

		activitiesApi
			.listPaginated(token, {
				filters,
				limit: 999999,
			})
			.then((res) => {
				const formatted: ActivityData[] = res.data
					.filter(
						(a) => appliedShowExistingActivities || (!a.beginDate && !a.endDate)
					)
					.map((a) => ({
						...a,
						badgeMovements: a.badgeMovements?.map((m) => ({
							...m,
							dateTime:
								m.dateTime instanceof Date
									? m.dateTime
									: m.dateTime
										? new Date(m.dateTime)
										: undefined,
							direction: m.direction || 'IN',
						})),
					}));
				setActivities(formatted);
			})
			.catch((err) => {
				console.error('Failed to load activities', err);
			})
			.finally(() => setLoadingActivity(false));
	}, [
		appliedSelectedBadge,
		appliedHeaderParams,
		appliedShowExistingActivities,
	]);

	// Gestion des mouvements de badge : on charge les mouvements de badge en fonction du badge sélectionné et des dates
	useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token || !selectedBadge) return;

		// attendre que les activités soient chargées pour charger les mouvements de badge, afin de pouvoir les rattacher aux activités
		if (loadingActivity) return;

		// Filtres pour l'API
		const filters: Record<string, string> = {
			dateTime_from: appliedHeaderParams.fromDate,
			dateTime_to: appliedHeaderParams.toDate,
		};

		badgeMovementsApi
			.listPaginated(token, { limit: 9999, filters })
			.then((res) => {
				// data est un tableau de tableaux → on aplatit
				const flatData: BadgeMovementData[] = res.data ?? [];
				const from = parseLocalDate(headerParams.fromDate);
				const to = parseLocalDate(headerParams.toDate);
				if (!from || !to) return;
				to.setHours(23, 59, 59, 999);

				// On regroupe les mouvements par activité pour les rattacher ensuite aux activités chargées
				const movementsByActivity = new Map<number, BadgeMovementData[]>();
				// On parcourt les mouvements de badge et on les classe par activité
				flatData.forEach((m) => {
					if (m.activity?.id) {
						if (!movementsByActivity.has(m.activity.id)) {
							movementsByActivity.set(m.activity.id, []);
						}
						movementsByActivity.get(m.activity.id)?.push(m);
					}
				});

				// On met à jour les activités avec les mouvements de badge correspondants
				setActivities((prev) =>
					prev.map((a) => ({
						...a,
						badgeMovements: movementsByActivity.get(a.id) ?? [],
					}))
				);

				// On filtre les mouvements de badge pour ne garder que ceux qui ne sont pas déjà rattachés à une activité (pour éviter les doublons dans la pool) et qui sont dans la période sélectionnée, puis on les formate pour les afficher dans la pool
				const formatted: BadgeMovementData[] = flatData
					.filter((m) => {
						const dt = parseLocalDate(m.dateTime);
						if (!dt) return false;
						return dt >= from && dt <= to;
					})
					.filter((m) => !m.activity)
					.map((m) => ({
						...m,
						dateTime: parseLocalDate(m.dateTime),
						direction: m.direction || 'IN',
					}));

				formatted.sort((a, b) => {
					const ta = a.dateTime ? new Date(a.dateTime).getTime() : 0;
					const tb = b.dateTime ? new Date(b.dateTime).getTime() : 0;
					return ta - tb;
				});

				setBadgeMovements(formatted);
			})
			.finally(() => setLoading(false));
	}, [loadingActivity]);

	const generateTemporaryId = () => {
		const id = nextTemporaryIdRef.current;
		nextTemporaryIdRef.current -= 1;
		return id;
	};

	const generateProposals = () => {
		const proposals: ActivityData[] = [];

		// 1. Grouper les mouvements par badgeNumber
		const movementsByBadge = new Map<string, BadgeMovementData[]>();

		badgeMovements.forEach((m) => {
			if (!m.badgeNumber || !m.dateTime) return;

			if (!movementsByBadge.has(m.badgeNumber)) {
				movementsByBadge.set(m.badgeNumber, []);
			}

			const list = movementsByBadge.get(m.badgeNumber);
			if (list) {
				list.push(m);
			} else {
				movementsByBadge.set(m.badgeNumber, [m]);
			}
		});

		// 2. Pour chaque badge
		movementsByBadge.forEach((movements) => {
			const sorted = [...movements].sort((a, b) => {
				const ta = a.dateTime ? new Date(a.dateTime).getTime() : 0;
				const tb = b.dateTime ? new Date(b.dateTime).getTime() : 0;
				return ta - tb;
			});

			// ---- Étape A : créer des segments stricts IN → OUT ----
			const segments: ActivityData[] = [];
			let currentIn: BadgeMovementData | null = null;

			sorted.forEach((m) => {
				if (!m.direction || !m.dateTime) return;

				if (m.direction === 'IN') {
					currentIn = m;
				} else if (m.direction === 'OUT' && currentIn && currentIn.dateTime) {
					segments.push({
						id: 0, // temporaire
						badgeMovements: [currentIn, m],
						beginDate: currentIn.dateTime,
						endDate: m.dateTime,
					});
					currentIn = null;
				}
			});

			if (segments.length === 0) return;

			// ---- Étape B : fusionner les segments ----
			let activityInProgress = { ...segments[0] };

			for (let i = 1; i < segments.length; i++) {
				const nextSegment = segments[i];

				if (
					!activityInProgress.beginDate ||
					!activityInProgress.endDate ||
					!nextSegment.beginDate ||
					!nextSegment.endDate
				)
					continue;

				const previousEnd = new Date(activityInProgress.endDate);
				const nextBegin = new Date(nextSegment.beginDate);

				const gapHours =
					(nextBegin.getTime() - previousEnd.getTime()) / 1000 / 3600;

				const newTotalDurationHours =
					(new Date(nextSegment.endDate).getTime() -
						new Date(activityInProgress.beginDate).getTime()) /
					1000 /
					3600;

				const shouldMerge =
					gapHours < minHoursBetweenActivity &&
					newTotalDurationHours <= maxHoursActivity;

				if (shouldMerge) {
					// fusion
					activityInProgress = {
						...activityInProgress,
						badgeMovements: [
							...(activityInProgress.badgeMovements || []),
							...(nextSegment.badgeMovements || []),
						],
						endDate: nextSegment.endDate,
					};
				} else {
					// push activité courante
					proposals.push({
						...activityInProgress,
						id: generateTemporaryId(),
					});

					// nouvelle activité
					activityInProgress = { ...nextSegment };
				}
			}

			// push dernière activité
			proposals.push({
				...activityInProgress,
				id: generateTemporaryId(),
			});
		});

		// 3. Assigner les services
		const proposalsWithService = proposals
			.map(assignServiceToActivity)
			.filter((a): a is ActivityData => !!a);

		setActivities((prev) => [...prev, ...proposalsWithService]);

		// 4. Retirer les mouvements utilisés
		const usedIds = proposals.flatMap(
			(a) => a.badgeMovements?.map((m) => m.id) || []
		);

		setBadgeMovements((prev) => prev.filter((m) => !usedIds.includes(m.id)));
	};

	// Fonction utilitaire pour mettre à jour les dates d'une activité selon ses mouvements de badge :
	// on trouve la date la plus ancienne parmi les mouvements IN et la date la plus récente parmi les mouvements OUT,
	// et on met à jour beginDate et endDate de l'activité en conséquence.
	// Cette fonction est appelée à chaque ajout ou suppression de mouvement dans une activité
	// pour s'assurer que les dates sont toujours cohérentes avec les mouvements qui lui sont rattachés.
	const updateActivityDates = (activity: ActivityData) => {
		const allDatesIn =
			activity.badgeMovements
				?.filter((m) => m.direction === 'IN')
				.map((m) =>
					m.dateTime instanceof Date
						? m.dateTime
						: m.dateTime
							? new Date(m.dateTime)
							: undefined
				)
				.filter((d): d is Date => d instanceof Date) || [];
		const allDatesOut =
			activity.badgeMovements
				?.filter((m) => m.direction === 'OUT')
				.map((m) =>
					m.dateTime instanceof Date
						? m.dateTime
						: m.dateTime
							? new Date(m.dateTime)
							: undefined
				)
				.filter((d): d is Date => d instanceof Date) || [];
		const beginDate =
			allDatesIn.length > 0
				? new Date(Math.min(...allDatesIn.map((d) => d.getTime())))
				: undefined;
		const endDate =
			allDatesOut.length > 0
				? new Date(Math.max(...allDatesOut.map((d) => d.getTime())))
				: undefined;
		activity.beginDate =
			beginDate instanceof Date && !isNaN(beginDate.getTime())
				? beginDate
				: activity.beginDate;
		activity.endDate =
			endDate instanceof Date && !isNaN(endDate.getTime())
				? endDate
				: activity.endDate;
	};

	// Ajouter un mouvement à l'activité actuellement sélectionnée
	const addToSelectedActivity = (movement: BadgeMovementData) => {
		if (!selectedActivityId) return;

		setActivities((prevActivities) =>
			prevActivities.map((activity) => {
				if (activity.id !== selectedActivityId) {
					return activity;
				}

				// On crée une nouvelle version de l'activité
				const updatedActivity = {
					...activity,
					badgeMovements: [...(activity.badgeMovements || []), movement],
				};

				// On met à jour ses dates
				updateActivityDates(updatedActivity);

				// On assigne le service en fonction de la durée (forfait / demi-forfait)
				assignServiceToActivity(updatedActivity);

				return updatedActivity;
			})
		);

		// On retire le mouvement de la pool
		setBadgeMovements((prev) => prev.filter((m) => m.id !== movement.id));
	};

	// Retirer un mouvement d'une activité donnée
	const removeFromActivity = (
		movement: BadgeMovementData,
		activity: ActivityData
	) => {
		setActivities((prevActivities) =>
			prevActivities.map((a) => {
				if (a.id !== activity.id) {
					return a;
				}

				const updatedActivity = {
					...a,
					badgeMovements: a.badgeMovements?.filter((m) => m.id !== movement.id),
				};

				updateActivityDates(updatedActivity);

				// On assigne le service en fonction de la nouvelle durée
				assignServiceToActivity(updatedActivity);

				return updatedActivity;
			})
		);

		// On remet le mouvement dans la pool
		setBadgeMovements((prev) => [...prev, movement]);
	};

	// Vérifier si une activité est entièrement valide : on considère qu'une activité est valide si elle a un id temporaire négatif et que sa validité est à true dans le state activitiesValidity (calculée dans AFBActivityCard)
	const isActivityFullyValid = (activity: ActivityData) => {
		return activity.id < 0 && activitiesValidity[activity.id];
	};

	// Gérer le changement de validité d'une activité : on met à jour le state activitiesValidity avec l'id de l'activité et sa validité
	const handleColorChange = (color: ActivityColor, activityId: number) => {
		setActivityColors((prev) => ({ ...prev, [activityId]: color }));
	};

	const toggleColorVisibility = (color: ActivityColor) => {
		setVisibleColors((prev) => ({
			...prev,
			[color]: !prev[color],
		}));
	};

	const handleApplyFilters = () => {
		// Vider les activités et pool
		setActivities([]);
		setBadgeMovements([]);
		setSelectedActivityId(null);
		setActivityColors({});

		// Appliquer les filtres
		setAppliedSelectedBadge(selectedBadge);
		setAppliedHeaderParams(headerParams);
		setAppliedShowExistingActivities(showExistingActivities);
	};

	// Calculer le nombre d'activités par couleur pour afficher dans le header
	const activityCounts = Object.values(activityColors).reduce(
		(acc, color) => {
			acc[color]++;
			return acc;
		},
		{ green: 0, yellow: 0, orange: 0, red: 0, blue: 0 }
	);

	// Sauvegarder une activité : via appel api POST si id temporaire negatif. C'est tout pour le moment.
	const saveActivity = async (activity: ActivityData) => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (isSavingActivity) {
			console.warn('Already saving an activity, please wait');
			return;
		}

		setIsSavingActivity(true);

		try {
			let savedActivity: ActivityData | undefined;
			// On force l'origin de l'activité à "BADGE"
			activity.origin = 'BADGE';
			// Si un id est présent, mise à jour de l'élément existant
			if (activity.id && activity.id > 0) {
				savedActivity = await activitiesApi.update(
					Number(activity.id),
					activity,
					token
				);
			} else {
				// Sinon, création d'un nouvel élément
				savedActivity = await activitiesApi.create(activity, token);
			}

			// Mettre à jour les badgeMovements pour rattacher l'activité
			if (savedActivity && activity.badgeMovements?.length) {
				await Promise.all(
					activity.badgeMovements.map((m) =>
						badgeMovementsApi.update(
							m.id,
							{ ...m, importedAt: undefined, activity: savedActivity },
							token
						)
					)
				);
			}

			// mettre à jour le state avec l'objet renvoyé par l'API, seulement si on a bien reçu un objet
			if (savedActivity) {
				const saved = savedActivity;
				setActivities((prev) =>
					prev.map((a) =>
						a.id === activity.id
							? { ...saved, badgeMovements: activity.badgeMovements }
							: a
					)
				);
			}
		} catch (error) {
			console.error('Failed to save activity', error);
		} finally {
			setIsSavingActivity(false);
		}
	};

	const saveAllValidActivities = async () => {
		const validActivities = activities.filter(isActivityFullyValid);

		for (const act of validActivities) {
			await saveActivity(act);
		}
	};

	const formatDateTime = (date?: Date | string) => {
		if (!date) return null;
		const d = new Date(date);
		const day = d.getDate().toString().padStart(2, '0');
		const month = (d.getMonth() + 1).toString().padStart(2, '0');
		const year = d.getFullYear();
		const hours = d.getHours().toString().padStart(2, '0');
		const minutes = d.getMinutes().toString().padStart(2, '0');
		const seconds = d.getSeconds().toString().padStart(2, '0');

		return (
			<>
				<strong>{`${day}/${month}/${year}`}</strong> {hours}:{minutes}:{seconds}
			</>
		);
	};

	const formatDayHeader = (date: Date) => {
		return date.toLocaleDateString('fr-FR', {
			weekday: 'long',
			day: 'numeric',
			month: 'long',
			year: 'numeric',
		});
	};

	// Fonction utilitaire : calcule la durée réelle d'une activité en minutes
	// en sommant les segments IN→OUT de ses mouvements de badge,
	// plutôt que de faire simplement endDate - beginDate (temps naïf incluant les pauses).
	const computeRealDurationMinutes = (
		activity: ActivityData
	): number | null => {
		const movements = activity.badgeMovements;

		// Fallback naïf si pas de mouvements
		if (!movements || movements.length === 0) {
			if (!activity.beginDate || !activity.endDate) return null;
			return (
				(new Date(activity.endDate).getTime() -
					new Date(activity.beginDate).getTime()) /
				1000 /
				60
			);
		}

		const sorted = [...movements]
			.filter((m) => m.dateTime && m.direction)
			.sort(
				(a, b) =>
					new Date(a.dateTime as Date).getTime() -
					new Date(b.dateTime as Date).getTime()
			);

		let totalMinutes = 0;
		let pendingIn: Date | null = null;

		for (const m of sorted) {
			const dt = new Date(m.dateTime as Date);
			if (m.direction === 'IN') {
				pendingIn = dt;
			} else if (m.direction === 'OUT' && pendingIn) {
				totalMinutes += (dt.getTime() - pendingIn.getTime()) / 1000 / 60;
				pendingIn = null;
			}
		}

		return totalMinutes > 0 ? totalMinutes : null;
	};

	// Fonction pour assigner un service à une activité selon sa durée : Forfait si durée >= 4h, Demi-forfait sinon
	const assignServiceToActivity = (activity: ActivityData) => {
		// Pré-requis : les services doivent être chargés pour pouvoir les assigner
		if (!serviceForfait || !serviceDemiForfait || !serviceESNonFacturable)
			return;

		// On utilise le temps réel (somme des segments IN→OUT) plutôt que le temps naïf
		const durationMinutes =
			computeRealDurationMinutes(activity) ??
			(activity.beginDate && activity.endDate
				? (new Date(activity.endDate).getTime() -
						new Date(activity.beginDate).getTime()) /
					1000 /
					60
				: 0);

		// On recupère prioritairement la référence du project de l'acivité si présente, sinon
		// on recupere la reference du premier projet rattaché au premier mouvement de badge
		const projectRef =
			activity.project?.referenceNumber ||
			activity.badgeMovements?.[0]?.project;
		// On vérifie d'abord si cette ref n'est pas sous la forme de 7 caractères finissants exactement par 'Q'
		const isESFacturable = projectRef && /^.{6}Q$/.test(projectRef);
		// si ce n'est pas le cas, on assigne le service ESN non facturable,
		// sinon on assigne le service Forfait ou Demi-forfait en fonction de la durée
		const chosenService =
			projectRef && !isESFacturable
				? serviceESNonFacturable
				: durationMinutes <= 60
					? serviceESNonFacturable
					: durationMinutes <= 240
						? serviceDemiForfait
						: serviceForfait;

		activity.service = { id: chosenService.id, label: chosenService.label };
		activity.unitPriceExclTax = chosenService.defaultPrice;

		// Normalize tax rate object to match Activity's TaxRateData shape (ensure id is a number)
		const defTax = chosenService.defaultTaxRate;
		activity.taxRate = defTax ? { ...defTax, id: defTax.id ?? 0 } : null;

		return activity;
	};

	const onProjectChange = (
		activityId: number,
		projectId: number,
		referenceNumber: string
	) => {
		setActivities((prev) =>
			prev.map((activity) => {
				if (activity.id !== activityId) return activity;

				// créer un nouvel objet pour la nouvelle activité
				const updatedActivity: ActivityData = {
					...activity,
					project: { id: projectId, referenceNumber },
					badgeMovements: activity.badgeMovements
						? [...activity.badgeMovements]
						: [],
				};

				// recalculer le service
				const withService = assignServiceToActivity(updatedActivity);

				// renvoyer le nouvel objet pour forcer le rerender
				return withService ?? updatedActivity;
			})
		);
	};

	// Gestion des changements dans le formulaire pour les selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'badge') {
			const selectedOption = badgesOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setSelectedBadge(selectedOption.key);
				// force le rechargement des mouvements de badge
				setLoading(true);
			}
			return;
		}
	};

	const formatOwnerLastName = (name?: string) => {
		if (!name) return '';
		const upper = name.toUpperCase();
		if (upper.length > 10) {
			return upper.slice(0, 10) + '.';
		}
		return upper;
	};

	return (
		<div className="p-6">
			{/* Titre de page */}
			<PageHeader
				icon={faPersonArrowDownToLine}
				title="Activités depuis Badges"
				subtitle="Sélectionnez les mouvements de badge dans la pool, regroupez-les en activités, puis sauvegardez"
				className="!max-w-full"
			/>
			<div className="p-4 bg-white rounded shadow max-w-16xl mx-auto mt-6">
				<div className="p-4 border rounded mb-6 bg-blue-50">
					<h3 className="font-semibold mb-4">
						Étape 1 : Sélection des données
					</h3>
					{/* Header */}
					<div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
						{/* Badge */}
						<div>
							<SelectField
								label="Badge"
								selectedKey={selectedBadge || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: { name: 'badge', value: val },
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={badgesOptions}
								loading={loading}
							/>
						</div>

						{/* Dates */}
						<div className="flex flex-col">
							<label className="text-sm font-medium text-gray-700">
								Date début
							</label>
							<input
								type="date"
								value={headerParams.fromDate}
								onChange={(e) =>
									setHeaderParams({ ...headerParams, fromDate: e.target.value })
								}
								className="border p-2 rounded"
							/>
						</div>
						<div className="flex flex-col">
							<label className="text-sm font-medium text-gray-700">
								Date fin
							</label>
							<input
								type="date"
								value={headerParams.toDate}
								onChange={(e) =>
									setHeaderParams({ ...headerParams, toDate: e.target.value })
								}
								className="border p-2 rounded"
							/>
						</div>

						{/* Case à cocher */}
						<div className="flex items-center mt-4 md:mt-0">
							<input
								id="showExisting"
								type="checkbox"
								checked={showExistingActivities}
								onChange={(e) => setShowExistingActivities(e.target.checked)}
								className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded"
							/>
							<label htmlFor="showExisting" className="text-sm text-gray-700">
								Charger les activités existantes
							</label>
						</div>

						{/* Bouton Appliquer avec un spinner de chargement */}
						<div className="flex items-center mt-4 md:mt-0">
							<button
								onClick={handleApplyFilters}
								className="bg-blue-600 text-white px-4 py-2 rounded"
								disabled={loadingActivity}
							>
								{loadingActivity ? (
									<FontAwesomeIcon icon={faSpinner} spin />
								) : (
									'Appliquer'
								)}
							</button>
						</div>
					</div>
				</div>

				<div className="p-4 border rounded bg-blue-50 mb-6">
					<h3 className="font-semibold mb-4">
						Étape 2 : Analyse et génération
					</h3>
					{/* Paramètres algorithme */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-6">
						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Min. heures entre 2 activités
								</label>
								<input
									type="number"
									placeholder="Min. heures"
									value={minHoursBetweenActivity}
									onChange={(e) =>
										setMinHoursBetweenActivity(Number(e.target.value))
									}
									className="border p-2 rounded"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Max. heures pour 1 activité
								</label>
								<input
									type="number"
									placeholder="Max. heures"
									value={maxHoursActivity}
									onChange={(e) => setMaxHoursActivity(Number(e.target.value))}
									className="border p-2 rounded"
								/>
							</div>
							<MainButton
								onClick={generateProposals}
								label="Générer les propositions"
								size="lg"
							/>
						</div>
						{/* Bouton et légende couleur */}
						<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
							<div className="flex flex-col gap-2 text-sm font-medium mt-2 md:mt-0">
								{colorLegend.map((item) => (
									<label
										key={item.key}
										className="flex items-center gap-3 cursor-pointer"
									>
										<input
											type="checkbox"
											checked={visibleColors[item.key]}
											onChange={() => toggleColorVisibility(item.key)}
										/>

										{/* Bloc couleur + texte */}
										<div
											className={`flex items-center gap-2 ${item.textClass}`}
										>
											<span className="w-16">{item.label}</span>

											{/* Nombre aligné */}
											<span className="w-10 text-right tabular-nums">
												{activityCounts[item.key] ?? 0}
											</span>

											{/* Description */}
											<span className="text-gray-500 text-xs ml-2">
												{item.description}
											</span>
										</div>
									</label>
								))}
							</div>
						</div>
					</div>
				</div>

				<div style={{ display: 'flex', gap: 24 }}>
					{/* Colonne 1 : Pool */}
					<div style={{ flex: 1 }}>
						<h4>Pool de mouvements</h4>
						{badgeMovements.map((m) => (
							<div
								key={m.id}
								style={{
									border: '1px solid #ccc',
									marginBottom: 4,
									padding: 4,
									backgroundColor: '#cbcbcbff',
									display: 'flex',
									alignItems: 'center',
									gap: 8,
								}}
							>
								{m.dateTime ? formatDateTime(m.dateTime) : 'Date manquante'}{' '}
								{m.direction === 'IN' ? (
									<FontAwesomeIcon
										icon={faArrowAltCircleRight}
										color="#28a745"
									/>
								) : (
									<FontAwesomeIcon
										icon={faArrowAltCircleLeft}
										color="#dc3545"
									/>
								)}
								{m.badgeNumber || 'Badge inconnu'}
								{m.ownerLastName && (
									<>{' ' + formatOwnerLastName(m.ownerLastName)}</>
								)}
								{/* Icon crayon permettant de naviguer vers /app/badgeMovements/${m.id}/edit */}
								<a
									href={`/app/badgeMovements/${m.id}/edit`}
									target="_blank"
									rel="noopener noreferrer"
								>
									<FontAwesomeIcon
										icon={faPen}
										className="w-4 h-4 text-gray-400 opacity-80 ml-2 cursor-pointer hover:text-gray-600"
									/>
								</a>
								{selectedActivityId && (
									<button
										onClick={() => addToSelectedActivity(m)}
										className="text-[#d400ff] hover:text-[#ff85ff] transition-colors"
									>
										<FontAwesomeIcon icon={faArrowRight} size="sm" />
									</button>
								)}
							</div>
						))}
					</div>

					{/* Colonne 2 : Activités proposées */}
					<div style={{ flex: 2.5 }} className="space-y-2">
						<h4>Activités</h4>
						{[...activities]
							.filter((a) => {
								const color = activityColors[a.id];
								if (!color) return true; // pas encore colorée → on affiche
								return visibleColors[color];
							})
							.sort((a, b) => {
								if (!a.beginDate) return 1;
								if (!b.beginDate) return -1;
								return (
									new Date(a.beginDate).getTime() -
									new Date(b.beginDate).getTime()
								);
							})
							.map((a, index, array) => {
								const currentDate = a.beginDate ? new Date(a.beginDate) : null;

								let showHeader = false;

								if (currentDate) {
									if (index === 0) {
										showHeader = true;
									} else {
										const prev = array[index - 1];
										const prevDate = prev.beginDate
											? new Date(prev.beginDate)
											: null;

										if (
											!prevDate ||
											prevDate.toDateString() !== currentDate.toDateString()
										) {
											showHeader = true;
										}
									}
								}

								return (
									<React.Fragment key={a.id}>
										{showHeader && currentDate && (
											<h1
												style={{
													marginTop: 24,
													marginBottom: 12,
													fontSize: 22,
													fontWeight: 700,
													borderBottom: '2px solid #ccc',
													paddingBottom: 4,
												}}
											>
												{formatDayHeader(currentDate)}
											</h1>
										)}

										<AFBActivityCard
											activity={a}
											projectOptions={projectOptions}
											isSelected={a.id === selectedActivityId}
											onSelect={() => {
												setSelectedActivityId(
													selectedActivityId === a.id ? null : a.id
												);
											}}
											onProjectChange={(projectId, referenceNumber) => {
												onProjectChange(a.id, projectId, referenceNumber);
											}}
											onRemoveMovement={(movement) => {
												removeFromActivity(movement, a);
											}}
											onSave={(activity) => {
												saveActivity(activity);
											}}
											onValidityChange={(isValid: boolean) =>
												setActivitiesValidity((prev) => ({
													...prev,
													[a.id]: isValid,
												}))
											}
											onColorChange={handleColorChange}
										/>
									</React.Fragment>
								);
							})}

						{/* Zone pour créer une nouvelle activité, sous forme d'un emplacement vide entouré de pointillés verts avec un texte au centre. Remplace le bouton ci-dessous*/}
						<div
							style={{
								border: '2px dashed #03b01dff',
								padding: 4,
								textAlign: 'center',
								cursor: 'pointer',
								marginTop: 8,
								borderRadius: 4,
								backgroundColor: '#b2e9c5ff',
							}}
							onClick={() => {
								const newActivity: ActivityData = {
									id: generateTemporaryId(),
									badgeMovements: [],
								};

								setActivities((prev) => [...prev, newActivity]);
								setSelectedActivityId(newActivity.id);
							}}
						>
							<span style={{ color: '#03b01dff' }}>
								+ Créer une nouvelle activité
							</span>
						</div>

						{activities.some((a) => isActivityFullyValid(a)) && (
							<MainButton
								label="Tout enregistrer (jaunes, oranges, vertes)"
								onClick={saveAllValidActivities}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ActivityFromBadge;
